import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import {
  Batch,
  Bundle,
  ProductionComponentInstance,
  ProductionComponentInstanceStatus,
  ProductionGroup,
  ScheduleLineGroup,
  ScheduleLineGroupData,
} from '@paragon/contract-library';

export type ProductionModelsState = {
  readonly activeScheduleLineGroupGuid: string | null;
  readonly orderedScheduleLineGroups: readonly ScheduleLineGroup[];
  readonly orderedBundlesByScheduleLineGroupGuid: {
    [scheduleGroupGuid: string]: readonly Bundle[];
  };
  readonly orderedProductionComponentInstancesByBundleGuid: {
    [bundleGuid: string]: readonly ProductionComponentInstance[];
  };
  readonly productionGroups: {
    [productionGroupGuid: string]: ProductionGroup;
  };
  readonly batches: {
    [batchGuid: string]: Batch;
  };
  readonly analysisSetGuidToComponentDesignGuid: {
    [analysisSetGuid: string]: string;
  };
};
const initialState: ProductionModelsState = {
  activeScheduleLineGroupGuid: null,
  orderedScheduleLineGroups: [],
  orderedBundlesByScheduleLineGroupGuid: {},
  orderedProductionComponentInstancesByBundleGuid: {},
  productionGroups: {},
  batches: {},
  analysisSetGuidToComponentDesignGuid: {},
};

const productionModelsSlice = createSlice({
  name: 'productionModels',
  initialState,
  reducers: {
    setActiveScheduleLineGroupGuid: (state, action: PayloadAction<string | null>) => {
      state.activeScheduleLineGroupGuid = action.payload;
    },
    scheduleLineGroupsLoaded: (state, action: PayloadAction<readonly ScheduleLineGroup[]>) => {
      state.orderedScheduleLineGroups = castDraft(action.payload);
    },
    scheduleGroupLineDataLoaded: (state, action: PayloadAction<ScheduleLineGroupData>) => {
      const {
        scheduleLineGroup,
        orderedBundles,
        orderedProductionComponentInstancesByBundleGuid,
        productionGroupsByGuid,
        batchesByGuid,
      } = action.payload;
      state.orderedBundlesByScheduleLineGroupGuid[scheduleLineGroup.guid] = castDraft(orderedBundles);
      for (const [bundleGuid, orderedProductionComponentInstances] of Object.entries(
        orderedProductionComponentInstancesByBundleGuid,
      )) {
        state.orderedProductionComponentInstancesByBundleGuid[bundleGuid] = castDraft(
          orderedProductionComponentInstances,
        );
      }
      for (const [productionGroupGuid, productionGroup] of Object.entries(productionGroupsByGuid)) {
        state.productionGroups[productionGroupGuid] = productionGroup;
      }
      for (const [batchGuid, batch] of Object.entries(batchesByGuid)) {
        state.batches[batchGuid] = batch;
      }
    },
    loadAnalysisSetGuidToComponentDesignGuid: (
      state,
      action: PayloadAction<{
        [analysisSetGuid: string]: string;
      }>,
    ) => {
      state.analysisSetGuidToComponentDesignGuid = { ...state.analysisSetGuidToComponentDesignGuid, ...action.payload };
    },
    setStatus: (
      state,
      action: PayloadAction<{ instanceGuids: readonly string[]; status: ProductionComponentInstanceStatus }>,
    ) => {
      const { instanceGuids, status } = action.payload;
      const instances = Object.values(state.orderedProductionComponentInstancesByBundleGuid)
        .flatMap((instances) => instances)
        .filter((instance) => instanceGuids.includes(instance.guid));
      for (const instance of instances) {
        instance.status = status;
      }
    },
  },
});

export const {
  loadAnalysisSetGuidToComponentDesignGuid,
  setActiveScheduleLineGroupGuid,
  scheduleLineGroupsLoaded,
  scheduleGroupLineDataLoaded,
  setStatus,
} = productionModelsSlice.actions;
export const productionModels = productionModelsSlice.reducer;
